import * as React from 'react';
import {
  BirthCertificateDocList,
  BirthCertificateDocs,
  DocAccessType,
  IDocumentsList,
  IRisk,
  ParentalConsentList,
  ParentalConsentMap,
} from '../../../../types';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';
import ParentalConsentSection from '../../../sections/documents/parental_consent';
import BirthCertificate from '../../../sections/documents/birthCertificate';
import { UserRole } from '../../../../store/auth/types';
import { useTranslation } from "react-i18next";

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  documents: ParentalConsentMap;
  birthCertificate: BirthCertificateDocs;
  warning?: React.ReactElement;
  userRole?: UserRole;

  canCheckOff?: boolean;
  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;
  canAcknowledge: boolean;
  showRisks: boolean;
  docRisk: IRisk['docsRisk'];

  isCheckedOff?: (docType: string, docKey: string) => boolean;
  onCheckOffRisk?: (docType: string, docKey: string, docTitle: string) => void;
  onResume: (url: string) => void;
  onUploadClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onOpenClick: (sponsorType: string, docType: string | null, docKey: string) => void;
  onApproveClick: (sponsorType: string, docType: string | null, docKey: string) => void;
  onRejectClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onRescindRejectClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onDeleteClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onAcknowledgeClick: (sponsorType: string, docType: string | null, docKey: string) => void;
}

const ParentalConsentHand = (props: IProps) => {
    const { t } = useTranslation('common');
  const { showRisks, infoOnly, userAccess, userRole } = props;
  const docs = props.documents ?? {};
  const birthCertificate = props.birthCertificate ?? {};
  let hasDocs = false;
  let hasDeletedDocs = false;
  const docNodes: React.ReactNode[] = [];

  if (docs) {
    const visibleDocs = getVisible({ documentsHash: docs, userAccess, infoOnly, userRole }) as ParentalConsentList[];
    if (hasDeleted(docs)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: ParentalConsentList, index: number) => {
          const { key, item } = doc;

          return (
            <FingerWithApprove
              id={`${key}`}
              key={`parental-consent-${index}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              isDocumentFixed={item.isDocumentFixed}
              reason={item.reason}
              label={t('common:parentConsentToTravelLetter', { index: Object.keys(docs).length > 1 ? (index > 0 ? index + 1 : '') : '' })}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() => (props.isCheckedOff ? props.isCheckedOff('parentalConsent', key) : false)}
              onCheckOffRisk={() =>
                props.onCheckOffRisk && props.onCheckOffRisk('parentalConsent', key, 'Parent consent to travel letter')
              }
              onUploadClick={() => props.onUploadClick('parentalConsent', null, key, 'Parental Consent')}
              onOpenClick={() => props.onOpenClick('parentalConsent', null, key)}
              onApproveClick={() => props.onApproveClick('parentalConsent', null, key)}
              onRejectClick={() => {
                props.onRejectClick('parentalConsent', null, key, `Parental Consent`);
              }}
              onRescindRejectClick={() => props.onRescindRejectClick('parentalConsent', null, key, `Parental Consent`)}
              onDeleteClick={() => {
                props.onDeleteClick('parentalConsent', null, key, `Parental Consent`);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('parentalConsent', null, key)}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              {item.status !== 'AWAITING' &&
              item.fileNames &&
              item.fileNames.length > 0 &&
              item.isDated !== undefined &&
              item.isSigned !== undefined ? (
                <ParentalConsentSection
                  id={key}
                  showRisks={showRisks}
                  risks={props.docRisk.parentalConsent}
                  isDated={item.isDated}
                  isSigned={item.isSigned}
                  uploadedBy={item.uploadedBy}
                />
              ) : undefined}
            </FingerWithApprove>
          );
        }),
      );
    }
  }

  if (birthCertificate && birthCertificate.certificate) {
    const visibleDocs = getVisible({
      documentsHash: birthCertificate.certificate!,
      userAccess,
      infoOnly,
      userRole,
    }) as BirthCertificateDocList[];
    if (hasDeleted(birthCertificate.certificate)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: BirthCertificateDocList, index: number) => {
          const { key, item } = doc;

          return (
            <FingerWithApprove
              id={`${key}`}
              key={`birth-certificate-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              isDocumentFixed={item.isDocumentFixed}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={t('common:birthCertificateOrProofOfGuardia', { index: Object.keys(birthCertificate).length > 1 ? (index > 0 ? index + 1 : '') : '' })}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() => (props.isCheckedOff ? props.isCheckedOff('birthCertificate', key) : false)}
              onCheckOffRisk={() =>
                props.onCheckOffRisk && props.onCheckOffRisk('birthCertificate', key, 'Birth Certificate')
              }
              onUploadClick={() =>
                props.onUploadClick(
                  'birthCertificate',
                  'certificate',
                  key,
                  'Birth Certificate or Proof of Guardianship',
                )
              }
              onOpenClick={() => props.onOpenClick('birthCertificate', 'certificate', key)}
              onApproveClick={() => props.onApproveClick('birthCertificate', 'certificate', key)}
              onRejectClick={() => {
                props.onRejectClick('birthCertificate', 'certificate', key, `Guardian's Birth Certificate Scan`);
              }}
              onRescindRejectClick={() =>
                props.onRescindRejectClick('birthCertificate', 'certificate', key, `Guardian's Birth Certificate Scan`)
              }
              onDeleteClick={() => {
                props.onDeleteClick('birthCertificate', 'certificate', key, `Guardian's Birth Certificate Scan`);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('birthCertificate', 'certificate', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              {item.status !== 'AWAITING' && item.fileNames && item.fileNames.length > 0 ? (
                <BirthCertificate
                  id={key}
                  showRisks={showRisks}
                  risk={props.docRisk.birthCertificate}
                  isLegalDocument={item.isLegalDocument!}
                  inEnglish={item.isInEnglish!}
                  hasTranslation={item.hasProvidedTranslation!}
                  uploadedBy={item.uploadedBy}
                />
              ) : undefined}
            </FingerWithApprove>
          );
        }),
      );
    }
  }

  if (birthCertificate && birthCertificate.translation) {
    const visibleDocs = getVisible({
      documentsHash: birthCertificate.translation,
      userAccess,
      infoOnly,
      userRole,
    }) as IDocumentsList[];
    if (hasDeleted(birthCertificate.translation)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;
          return (
            <FingerWithApprove
              id={`${key}`}
              key={`birth-certificate-translation-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={t('common:birthCertificateTranslation', { index: Object.keys(birthCertificate).length > 1 ? (index > 0 ? index + 1 : '') : '' })}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() => (props.isCheckedOff ? props.isCheckedOff('birthCertificate', key) : false)}
              onCheckOffRisk={() =>
                props.onCheckOffRisk && props.onCheckOffRisk('birthCertificate', key, 'Birth Certificate Translation')
              }
              onUploadClick={() =>
                props.onUploadClick('birthCertificate', 'translation', key, 'Birth Certificate Translation')
              }
              onOpenClick={() => props.onOpenClick('birthCertificate', 'translation', key)}
              onApproveClick={() => props.onApproveClick('birthCertificate', 'translation', key)}
              onRejectClick={() => {
                props.onRejectClick('birthCertificate', 'translation', key, `Birth Certificate Translation`);
              }}
              onRescindRejectClick={() =>
                props.onRescindRejectClick('birthCertificate', 'translation', key, `Birth Certificate Translation`)
              }
              onDeleteClick={() => {
                props.onDeleteClick('birthCertificate', 'translation', key, `Birth Certificate Translation`);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('birthCertificate', 'translation', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        }),
      );
    }
  }

  const getParentalConsentWarnings = () => {
    let warnings = null;
    const visibleDocs = getVisible({ documentsHash: docs, userAccess, infoOnly, userRole }) as ParentalConsentList[];
    if (visibleDocs.length === 0) {
      return warnings;
    }
    const lastVisibleDoc: ParentalConsentList = visibleDocs[visibleDocs.length - 1];
    const { item } = lastVisibleDoc;
    if (item.isDocumentFixed || (item.isSigned === undefined && item.isDated === undefined)) {
      return warnings;
    }
    if (item.isSigned === false && item.isDated === false) {
      warnings = (
        <>
          <p>{t('common:yourLetterMustBeSignedByBothPare')}</p>
          <p>{t('common:yourLetterMustBeDated')}</p>
        </>
      );
    } else if (item.isSigned === false) {
      warnings = (
        <p>{t('common:yourLetterMustBeSignedByBothPare')}</p>
      );
    } else if (item.isDated === false) {
      warnings = <p>{t('common:yourLetterMustBeDated')}</p>;
    }
    return warnings;
  };

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if ((infoOnly || userAccess) && !hasDocs) {
    return null;
  }

  return (
    <Section
      label={t('common:youIndicatedYouWillBeUnder18When')}
      canEdit={props.canResume}
      edit={() => {
        props.onResume('personal-info/step-2');
      }}
      warning={getParentalConsentWarnings()}
    >
      {hasDocs ? docNodes : <NoDocuments />}
    </Section>
  );
};

export default ParentalConsentHand;
